import React, { useState, useEffect } from 'react';
import './Wiki.css';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import NinoxService from '../../services/Ninox';
import { NinoxDataRow, NinoxField, NinoxCol } from '../../types/Ninox';

const columnWidths: Record<string, string> = {
  'Hersteller':                         '80px',
  'Klebstoff':                          '90px',
  'Kleberkategorie':                    '120px',
  'Kleberart':                          '90px',
  'Klebereigenschaften':                '160px',
  'Beschreibung':                       '600px',
  'Branche':                            '120px',
  'Anwendung Material':                 '100px',
  'Tack (Anfangshaftung) (N/25mm)':     '120px',
  'Peel adhesion (Endhaftung)':         '120px',
  'Köhäsion / Scherfestigkeit':         '120px',
  'Mindestverklebetemperatur (°C)':      '120px',
  'minimale Anwendungstemperatur (°C)':  '120px',
  'maximale Anwendungstemperatur (°C)':  '120px',
  'Bermerkungen':                        '130px',
};

// Columns the user can sort by, and how their values should be compared.
const sortableColumns: Record<string, 'text' | 'number'> = {
  'Kleberkategorie':                    'text',
  'Kleberart':                          'text',
  'Klebereigenschaften':                'text',
  'Mindestverklebetemperatur (°C)':     'number',
  'minimale Anwendungstemperatur (°C)': 'number',
  'maximale Anwendungstemperatur (°C)': 'number',
};

// Extract a plain string value from a Ninox cell, which may be a raw value or an object.
const getCellText = (cellValue: NinoxDataRow['fields'][string]): string => {
  if (cellValue === null || cellValue === undefined) {
    return '';
  }
  if (typeof cellValue === 'object') {
    return cellValue.value !== undefined && cellValue.value !== null
      ? String(cellValue.value)
      : JSON.stringify(cellValue);
  }
  return String(cellValue);
};

export default function Wiki () {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [cols, setCols] = useState<NinoxCol[]>([]);
  // Store each table's data rows in an object keyed by team/table id.
  const [tableData, setTableData] = useState<Record<string, NinoxDataRow[]>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const { t } = useTranslation(['main']);

  // Cycle a column's sort through three states on repeated clicks: asc -> desc -> unsorted.
  const handleSort = (fieldName: string) => {
    if (sortField !== fieldName) {
      setSortField(fieldName);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      // Was descending -> clear back to the default (unsorted) state.
      setSortField(null);
      setSortDir('asc');
    }
  };
  const fieldsOrder = [0, 1, 13, 2, 14, 15, 16, 17, 18, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  useEffect(() => {
    // First, fetch the table definitions (columns and team metadata)
    NinoxService.fetchColumns()
      .then((colsData: NinoxCol[]) => {
        const filteredCols = colsData.filter(col => col.name && col.name.includes('KleberWiki'));
        const colsWithOrderedFields = filteredCols.map((col, idx) => {
          if (idx < 5) {
            return {
              ...col,
              fields: fieldsOrder.map(i => col.fields[i]).filter(Boolean),
            };
          }
          return col;
        });
        setCols(colsWithOrderedFields);
        // For each team, fetch the row data.
        return Promise.all(
          filteredCols.map(team =>
            NinoxService.fetchDataForTable(team.id).then((rows: NinoxDataRow[]) => ({
              teamId: team.id,
              rows,
            })),
          ),
        );
      })
      .then(results => {
        // Build an object mapping team id to its rows
        const dataMap: Record<string, NinoxDataRow[]> = {};
        results.forEach(({ teamId, rows }) => {
          dataMap[teamId] = rows;
        });
        setTableData(dataMap);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching columns or table data:', err);
        setLoading(false);
      });
  }, []);

  // Helper function to extract team name from fields array or directly from team.name
  const getTabName = (team: NinoxCol): string => {
    if (team.name) {
      const result = team.name.replace('KleberWiki ', '').trim();
      return result;
    }
    const nameField = team.fields.find((field: NinoxField) =>
      field.name.toLowerCase().includes('team') ||
        field.name.toLowerCase() === 'name',
    );
    return nameField && nameField.value ? String(nameField.value) : team.id || 'Unknown Team';
  };

  const showTab = (index: number) => {
    setActiveTab(index);
  };

  if (loading) {
    return (
      <div className="flex w-full h-full justify-center align-center">
        <p
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          {t('main:wiki.loading')}
        </p>
      </div>
    );
  }


  if (cols.length === 0) {
    return (
      <p className="no-data">
        {t('main:wiki.empty')}
      </p>
    );
  }

  return (
    <div className="wiki-container">
      <h1 className="wiki-title">
        {t('main:wiki.title')}
      </h1>

      {/* Tabs Navigation */}
      <div className="tabs">
        {cols.map((team, index) => (
          <div
            key={team.id}
            className={`tab ${activeTab === index ? 'active' : ''}`}
            onClick={() => showTab(index)}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && showTab(index)}
            role="button"
            tabIndex={0}
          >
            {getTabName(team)}
          </div>
        ))}
      </div>

      {/* Tab Content */}
      {cols.map((tab, index) => {
        const rows = tableData[tab.id] ?? [];
        const query = search.trim().toLowerCase();
        const filteredRows = query
          ? rows.filter(row =>
            getCellText(row.fields ? row.fields['Klebstoff'] : '')
              .toLowerCase()
              .includes(query),
          )
          : rows;
        const sortType = sortField ? sortableColumns[sortField] : undefined;
        const sortedRows = sortType
          ? [...filteredRows].sort((a, b) => {
            const aText = getCellText(a.fields ? a.fields[sortField as string] : '');
            const bText = getCellText(b.fields ? b.fields[sortField as string] : '');
            let cmp: number;
            if (sortType === 'number') {
              const aNum = parseFloat(aText.replace(',', '.'));
              const bNum = parseFloat(bText.replace(',', '.'));
              const aValid = !Number.isNaN(aNum);
              const bValid = !Number.isNaN(bNum);
              // Push empty / non-numeric values to the bottom regardless of direction.
              if (!aValid && !bValid) {
                cmp = 0;
              } else if (!aValid) {
                return 1;
              } else if (!bValid) {
                return -1;
              } else {
                cmp = aNum - bNum;
              }
            } else {
              cmp = aText.localeCompare(bText, undefined, { sensitivity: 'base' });
            }
            return sortDir === 'asc' ? cmp : -cmp;
          })
          : filteredRows;
        return (
          <div key={tab.id} className={`tab-content ${activeTab === index ? 'active' : ''}`}>
            <div className="wiki-tab-header">
              <div className="wiki-search-wrapper">
                <SearchIcon className="wiki-search-icon" fontSize="small" />
                <input
                  type="text"
                  className="wiki-search-input"
                  placeholder={t('main:wiki.searchPlaceholder') ?? ''}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <h2>
                {getTabName(tab)}
              </h2>
            </div>
            <div className="table-container">
              <table className="table-body">
                <colgroup>
                  {tab.fields.map((field: NinoxField) => (
                    <col key={field.id} style={{ width: columnWidths[field.name] ?? '300px' }} />
                  ))}
                </colgroup>
                <thead>
                  <tr className="table-col">
                    {tab.fields.map((field: NinoxField) => {
                      const isSortable = field.name in sortableColumns;
                      const isActive = sortField === field.name;
                      return (
                        <th
                          key={field.id}
                          className={`table-header${isSortable ? ' sortable' : ''}`}
                          onClick={isSortable ? () => handleSort(field.name) : undefined}
                          role={isSortable ? 'button' : undefined}
                          tabIndex={isSortable ? 0 : undefined}
                          onKeyDown={isSortable
                            ? e => (e.key === 'Enter' || e.key === ' ') && handleSort(field.name)
                            : undefined}
                        >
                          {isSortable ? (
                            <span className="header-sort-layout">
                              {isActive ? (
                                <span className="sort-arrows single">
                                  {sortDir === 'asc' ? '▲' : '▼'}
                                </span>
                              ) : (
                                <span className="sort-arrows">
                                  <span className="arrow">
                                    ▲
                                  </span>
                                  <span className="arrow">
                                    ▼
                                  </span>
                                </span>
                              )}
                              <span className="table-header-label">
                                {field.name}
                              </span>
                            </span>
                          ) : (
                            <span className="table-header-label">
                              {field.name}
                            </span>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  { tableData ? (
                    sortedRows.length > 0 ? (
                      sortedRows.map((row: NinoxDataRow, idx: number) => (
                        <tr key={idx}>
                          {tab.fields.map((field: NinoxField) => {
                          // Get the cell value from the row.fields using the field name
                            const cellValue = row.fields ? row.fields[field.name] : '';
                            return (
                              <td key={field.id} className="table-row">
                                {getCellText(cellValue)}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    ) : (
                      <tr className="table-col">
                        <td colSpan={tab.fields.length} className="no-data h-5">
                          <p className="container">
                            {t('main:wiki.empty')}
                          </p>
                        </td>
                      </tr>
                    )
                  ) : (
                    <tr className="table-col">
                      <td colSpan={tab.fields.length} className="no-data">
                        {t('main:wiki.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
